<template>
    <CCard class="mb-0 h-100" v-show="!storePageLoading">
        <CCardHeader>
            <CRow>
                <CCol>
                    <h1 class="page__title">{{ $t(titlePage) }}</h1>
                </CCol>
                <CCol class="d-flex justify-content-end">
                    <ButtonCreate
                        :title="$t('Add domain to blacklist')"
                        @click="modals.addDomainToBlacklist.show = true"
                    >
                        {{ $t("Add domain") }}
                    </ButtonCreate>
                </CCol>
            </CRow>
        </CCardHeader>
        <CCardBody class="main-card-body">
            <CContainer fluid class="p-0">
                <CRow class="p-2 m-0">
                    <CCol md="12" sm="12">
                        <!-- Filter -->
                        <FilterFields
                            :fields="tables.domainBlacklist.fields"
                            @filter="updateData"
                        />
                    </CCol>
                </CRow>
                <CRow
                    v-show="
                        hasItemsInTable({
                            tableName: 'domainBlacklist',
                        })
                    "
                >
                    <CCol xl="12" md="12" class="mb-4">
                        <CCard>
                            <CCardHeader>
                                <div class="d-flex">
                                    <TableSettings
                                        :allFields="
                                            tables.domainBlacklist.allFields
                                        "
                                        :activeFields.sync="
                                            tables.domainBlacklist.fields
                                        "
                                    />
                                </div>
                            </CCardHeader>
                            <CCardBody>
                                <CDataTable
                                    :addTableClasses="['table--sm']"
                                    :items="tables.domainBlacklist.items"
                                    :fields="
                                        traslateArrayObjects({
                                            array: tables.domainBlacklist
                                                .fields,
                                            fields: ['label'],
                                        })
                                    "
                                    :items-per-page="
                                        tables.domainBlacklist.rows
                                    "
                                    :loading="tables.domainBlacklist.loading"
                                    v-bind="tables.domainBlacklist"
                                    :columnFilterValue.sync="
                                        tables.domainBlacklist.columnFilterValue
                                    "
                                    :sorterValue.sync="
                                        tables.domainBlacklist.sorterValue
                                    "
                                    @update:column-filter-value="updateData()"
                                    @update:sorter-value="updateData()"
                                    @pagination-change="itemPerPage"
                                >
                                    <template #under-table>
                                        <div class="d-flex justify-content-end">
                                            <ButtonExport
                                                endPoint="/headers.api/blacklist/domains"
                                                :table="tables.domainBlacklist"
                                            />
                                        </div>
                                    </template>
                                    <template #_selected="{ item }">
                                        <td class="py-2">
                                            <CInputRadio
                                                name="_selected"
                                                :checked="
                                                    item ===
                                                    tables.domainBlacklist
                                                        .itemSelected
                                                "
                                                @update:checked="
                                                    selectItemTable(
                                                        'domainBlacklist',
                                                        item
                                                    )
                                                "
                                            />
                                        </td>
                                    </template>
                                </CDataTable>
                            </CCardBody>
                            <CCardFooter
                                v-if="tables.domainBlacklist.total_pages > 0"
                            >
                                <CPagination
                                    @update:activePage="updatePagination"
                                    :activePage="tables.domainBlacklist.page"
                                    :pages="tables.domainBlacklist.total_pages"
                                    align="center"
                                />
                            </CCardFooter>
                        </CCard>
                    </CCol>
                </CRow>
                <AddDomainToBlacklistModal
                    :show.sync="modals.addDomainToBlacklist.show"
                    v-bind="modals.addDomainToBlacklist"
                    v-on:submit="afterDomainAddingToBlacklist"
                    :title="$t('Add domain to blacklist')"
                />
            </CContainer>
            <NoItemsContainer
                v-show="
                    !hasItemsInTable({
                        tableName: 'domainBlacklist',
                    })
                "
            >
                <template #title>
                    {{ $t("Add domain to blacklist") }}
                </template>
                <template #description>
                    {{ $t("You can add an domain to blacklist.") }}
                </template>
                <template #redirect>
                    <ButtonCreate
                        @click="modals.addDomainToBlacklist.show = true"
                    >
                        {{ $t("Add domain") }}
                    </ButtonCreate>
                </template>
                <template #cleanFilter>
                    <CButton
                        color="primary"
                        class="button"
                        v-show="
                            hasFiltersInTable({
                                tableName: 'domainBlacklist',
                            })
                        "
                        @click="
                            cleanFiltersAdvance('domainBlacklist'), updateData()
                        "
                    >
                        <span class="button__icon">
                            <CIcon :content="$options.icons.eraser" />
                        </span>
                        <span class="button__text">
                            {{ $t("Clean filters") }}
                        </span>
                    </CButton>
                </template>
            </NoItemsContainer>
        </CCardBody>
    </CCard>
</template>

<script>
/** Components */
import FilterDate from "~/components/FilterDate";
import ButtonCreate from "~/components/ButtonCreate";
import TableSettings from "£lement/TableSettings";
import ButtonExport from "~/components/ButtonExport";
import FilterFields from "~/components/FilterFields";
import NoItemsContainer from "~/components/NoItemsContainer";
import AddDomainToBlacklistModal from "../Components/AddDomainToBlacklistModal.vue";

/** Mixins */
import pageMixin from "~/mixins/page.js";
import tableMixin from "~/mixins/table.js";

/** Others */
import {
    cilPlus,
    cilSettings,
    cilReload,
    cilPencil,
    cilChart,
    cilTrash,
    cilArrowThickFromRight,
    cilMediaStop,
    cilMediaPlay,
} from "@coreui/icons";

import DomainBlacklistTable from "../Config/Tables/DomainBlacklist";

export default {
    name: "DomainsBlacklistPage",
    middleware: ["auth", "page"],
    mixins: [pageMixin, tableMixin],
    components: {
        FilterDate,
        ButtonCreate,
        TableSettings,
        ButtonExport,
        FilterFields,
        NoItemsContainer,
        AddDomainToBlacklistModal,
    },
    icons: {
        cilPlus,
        cilSettings,
        cilPencil,
        cilTrash,
        cilArrowThickFromRight,
        cilReload,
        cilChart,
        cilMediaPlay,
        cilMediaStop,
    },
    data() {
        return {
            tables: {
                domainBlacklist: DomainBlacklistTable,
            },
            modals: {
                addDomainToBlacklist: {
                    show: false,
                    color: "primary",
                    size: "lg",
                },
            },
        };
    },
    computed:{
        titlePage: function () {
            return "Domains blacklist";
        },
    },
    methods: {
        itemPerPage(newValue) {
            this.tables.domainBlacklist.rows = newValue;
            this.updatePagination(this.tables.domainBlacklist.page);
        },
        async updatePagination(newValue) {
            this.tables.domainBlacklist.page = newValue;
            await this.updateData();
        },
        async updateData() {
            await this.updateTable({
                tableName: "domainBlacklist",
                url: "/headers.api/blacklist/domains",
            });
            this.tables.domainBlacklist.itemSelected = null;
            this.tables.domainBlacklist.loading = false;
            this.updatePageLoading(false);
        },

        afterDomainAddingToBlacklist(response) {
            this.modals.addDomainToBlacklist.show = false;
            this.updateData();
        },
    },
    async mounted() {
        await this.updateData();
    },
};
</script>

<style scoped lang="scss">
</style>